import { isServer, useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useI18nAddon } from '@/i18n/i18n'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useAPI } from '@/utils/requests/api-plugin'

import type { SearchStatusResult } from '../types'
import { mapSearchApiResults } from '../utils'

export function useSearch() {
    const inputValue = ref('')
    const isSearchOpened = ref(false)
    const loggerStore = useLoggerStore()
    const layoutStore = useLayoutStore()
    const i18nAddon = useI18nAddon()
    const router = useRouter()
    const { search: doSearch } = useAPI()

    function toggleSearch(value: boolean) {
        isSearchOpened.value = value
        layoutStore.setFullscreen(value)
    }

    function handleOpen() {
        toggleSearch(true)
    }

    function handleClose() {
        toggleSearch(false)
    }

    function onUpdate(value: string) {
        inputValue.value = value
    }

    function onItemClick() {
        // here we want to "commit" the search to make some additional logic on portal-back
        doSearch({
            q: inputValue.value,
            num: '10',
            hl: i18nAddon.currentLocale.value,
            lr: `lang_${i18nAddon.getCurrentLanguage()}`,
            commit: 'true',
        })
    }

    function reset() {
        inputValue.value = ''
    }

    const queryEnabled = computed(() => inputValue.value.trim().length > 1 && !isServer)
    const queryKey = computed(() => ['portal-search', inputValue.value, i18nAddon.currentLocale.value])

    const {
        isError,
        isLoading,
        isSuccess,
        data: searchData,
    } = useQuery({
        queryKey,
        enabled: queryEnabled,
        queryFn: async () => {
            const result = await doSearch({
                q: inputValue.value,
                num: '10',
                hl: i18nAddon.currentLocale.value,
                lr: `lang_${i18nAddon.getCurrentLanguage()}`,
            })

            if (result.status !== 'ok') {
                throw new Error(result.status === 'error' ? result.message : `HTTP ${result.httpStatus}`)
            }

            return mapSearchApiResults({
                query: inputValue.value,
                data: result.data,
                resolveRoute: (route: string) => router.resolve(route),
            })
        },
        // do not ddos our proxy when exceeding quota
        retry: 1,
    })

    const status = computed((): SearchStatusResult => {
        if ((searchData.value && searchData.value.result.length === 0) || isError.value) {
            return 'empty'
        }

        if (searchData.value) {
            return 'loaded'
        }

        if (isLoading.value && inputValue.value) {
            return 'loading'
        }

        return 'initial'
    })

    watch(isSuccess, (success) => {
        if (success && searchData.value) {
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'view_search_results',
                dimension1: String(searchData.value.totalItems),
                dimension2: searchData.value.queryTerm,
            })
        }
    })

    return {
        inputValue,
        isSearchOpened,
        searchData,
        status,
        isLoading,
        isError,

        handleClose,
        handleOpen,
        onUpdate,
        onItemClick,
        reset,
    }
}
